<template>
  <div v-if="loading" ref="refHeight" class="loading-container">
    <div class="d-flex justify-content-center mt-5">
      <div class="spinner-border" role="status" style="width: 3rem; height: 3rem;">
        <span class="visually-hidden">{{ content?.catalogueTraduction?.globalChargement || $t('global.chargement') }}</span>
      </div>
    </div>
  </div>
  <div v-else-if="hasError" ref="refHeight" class="error-container">
    <div class="m-5 alert alert-danger" role="alert">
      <h4 class="alert-heading">{{ content?.catalogueTraduction?.errorTitre || $t('error.titre') }}</h4>
      <p>{{ content?.catalogueTraduction?.errorMessage || $t('error.message') }}</p>
    </div>
  </div>
  <template v-else>
    <div id="catalogue" ref="refHeight" class="m-5">
      <div class="ns-banner">
        <div v-if="content.couverture" class="ns-banner-picture">
          <img :alt="content.couverture.alt" :src="widgetURL + content.couverture.url" class="card-img-top"/>
        </div>
        <div class="ns-banner-wrap">
          <h1 class="titre">{{ content.libelle }}</h1>
          <div v-if="content.descriptif" class="descriptif" v-html="content.descriptif"></div>
        </div>
      </div>

      <div v-if="content.filtrePreliminaire && !filtre.preFiltreLoad" class="messageFiltres alert alert-warning mt-3"
           role="alert">
        {{ content?.catalogueTraduction?.catalogueMessagePreliminaire || $t('catalogue.messagePreliminaire') }}
      </div>

      <nav class="ns-filters navbar">
        <form ref="formFiltres" class="container-fluid justify-content-center" @submit.prevent>
          <template v-for="f in content.filtres" :key="f.id">

            <div v-if="f.id === constants.filtre.FILTRE_MOT_CLE && f.actif
                        &&  (filtre.preFiltreLoad || f.preliminaire)" id="filtreMotCle"
                 class="ns-filters-keywords me-3">
              <input id="keywords" v-model="filtre.motCle" :required="f.preliminaire" class="form-control" name="motCle"
                     :placeholder="content?.catalogueTraduction?.catalogueMotCle || $t('catalogue.motCle')" type="text" @blur.passive="onFiltreInputBlur(f.preliminaire, $event)"
                     @keyup.passive="onFilter()">
              <div v-if="f.preliminaire" class="invalid-feedback">{{ content?.catalogueTraduction?.catalogueObligatoire || $t('catalogue.obligatoire') }}</div>
            </div>

            <div
                v-else-if="f.id === constants.filtre.FILTRE_DATE && f.actif && (filtre.preFiltreLoad || f.preliminaire)"
                class="dates form-group me-3">
              <div class="ns-filters-dates form-control ns-btn">
                <button class="ns-btn-filter" @click.passive="onToggleCalendar($event)">{{ content?.catalogueTraduction?.catalogueDates || $t('catalogue.dates') }}<i
                    class="ns-btn-filter-icon ns-btn-filter-icon-dates"></i></button>
                <div class="ns-btn-menu">
                  <Datepicker v-model="datesInterval"
                              :enableTimePicker="false"
                              :format-locale="fr"
                              autoApply
                              format="dd/MM/yyyy"
                              hideInputIcon
                              inline
                              :locale="content?.catalogueTraduction?.langue?.split('_')[0] || 'fr'"
                              multiCalendars
                              range/>
                </div>
              </div>
            </div>

            <div
                v-else-if="f.id === constants.filtre.FILTRE_PUBLIC && f.actif && (filtre.preFiltreLoad || f.preliminaire)"
                id="filtrePublic" class="form-group me-3">
              <select id="typePublic" v-model="filtre.typePublic" :required="f.preliminaire" class="form-select"
                      name="typePublic" @blur.passive="onFiltreInputBlur(f.preliminaire, $event)"
                      @change.passive="onFilter()">
                <option value="">{{ content?.catalogueTraduction?.tousTypePublique || $t('catalogue.tous.typePublique') }}</option>
                <option v-for="pub in content.publics" :key="pub.id" :value="pub.id">{{ pub.nom }}</option>
              </select>
              <div v-if="f.preliminaire" class="invalid-feedback">{{ content?.catalogueTraduction?.catalogueObligatoire || $t('catalogue.obligatoire') }}</div>
            </div>

            <div
                v-else-if="f.id === constants.filtre.FILTRE_THEME && f.actif && (filtre.preFiltreLoad || f.preliminaire)"
                id="filtreThemes" class="form-group me-3 ns-btn">
              <i class="ns-btn-filter-icon ns-btn-filter-icon-themes"></i>
              <select id="theme" v-model="filtre.theme" :required="f.preliminaire" class="form-select"
                      name="typeTheme" @blur.passive="onFiltreInputBlur(f.preliminaire, $event)"
                      @change.passive="onFilter()">
                <option value="">{{ content?.catalogueTraduction?.tousThemes || $t('catalogue.tous.themes') }}</option>
                <option v-for="theme in content.themes" :key="theme.id" :value="theme.id">{{ theme.libelle }}</option>
              </select>
              <div v-if="f.preliminaire" class="invalid-feedback">{{ content?.catalogueTraduction?.catalogueObligatoire || $t('catalogue.obligatoire') }}</div>
            </div>

            <div
                v-else-if="f.id === constants.filtre.FILTRE_HANDICAPE && f.actif && (filtre.preFiltreLoad || f.preliminaire)"
                id="filtreHandicap" class="form-group me-3">
              <div class="ns-filters-checkbox">
                <input id="accesHandicap" v-model="filtre.accesHandicap" :required="f.preliminaire"
                       class="form-check-input me-1"
                       name="accesHandicap"
                       type="checkbox" @blur.passive="onFiltreInputBlur(f.preliminaire, $event)"
                       @change.passive="onFilter()"/>
                <label class="form-check-label" for="accesHandicap">
                  {{ content?.catalogueTraduction?.catalogueAccesHandicape || $t('catalogue.accesHandicape') }}
                  <i v-if="f.preliminaire" class="bi bi-asterisk text-danger"></i>
                  <span :title="content?.catalogueTraduction?.formAnijhan || $t('form.anijhan')"
                        class="bi-info-circle"
                        data-bs-placement="right"
                        data-bs-toggle="tooltip"></span>
                </label>
              </div>
              <div v-if="f.preliminaire" class="invalid-feedback">{{ content?.catalogueTraduction?.catalogueObligatoire || $t('catalogue.obligatoire') }}</div>
            </div>

            <div v-else-if="f.id === constants.filtre.FILTRE_AGE && f.actif && (filtre.preFiltreLoad || f.preliminaire)"
                 id="filtreAnnee" class="form-group me-3 ns-btn">
              <i class="ns-btn-filter-icon ns-btn-filter-icon-birthdate"></i>
              <input id="anneeNaissance" v-model="filtre.anneeNaissance" v-maska="['####', '####']"
                     :placeholder="content?.catalogueTraduction?.catalogueFormatAnnee || $t('catalogue.formatAnnee')" :required="f.preliminaire"
                     class="form-control"
                     min="1900"
                     name="anneeNaissance" type="number"
                     @blur.passive="onFiltreInputBlur(f.preliminaire, $event)" @keyup.passive="onFilter()"
                     @change.passive="onFilter()"/>
              <div v-if="f.preliminaire" class="invalid-feedback">{{ content?.catalogueTraduction?.catalogueObligatoire || $t('catalogue.obligatoire') }}</div>
            </div>

            <div
                v-else-if="f.id === constants.filtre.FILTRE_MOBILITE && f.actif && (filtre.preFiltreLoad || f.preliminaire)"
                id="filtreMobilite" class="form-group me-3">
              <select id="mobilite" v-model="filtre.mobilite" :required="f.preliminaire" class="form-select"
                      name="mobilite" @blur.passive="onFiltreInputBlur(f.preliminaire, $event)"
                      @change.passive="onFilter()">
                <option value="">{{ content?.catalogueTraduction?.tousMobilites || $t('catalogue.tous.mobilites') }}</option>
                <option v-for="mobilite in content.mobilites" :key="mobilite.id" :value="mobilite.id">
                  {{ mobilite.libelle }}
                </option>
              </select>
              <div v-if="f.preliminaire" class="invalid-feedback">{{ content?.catalogueTraduction?.catalogueObligatoire || $t('catalogue.obligatoire') }}</div>
            </div>

            <div
                v-else-if="f.id === constants.filtre.FILTRE_RYTHME && f.actif && (filtre.preFiltreLoad || f.preliminaire)"
                id="filtreRythme" class="form-group me-3">
              <select id="rythme" v-model="filtre.rythme" :required="f.preliminaire" class="form-select"
                      name="rythme" @blur.passive="onFiltreInputBlur(f.preliminaire, $event)"
                      @change.passive="onFilter()">
                <option value="">{{ content?.catalogueTraduction?.tousTythmes || $t('catalogue.tous.rythmes') }}</option>
                <option v-for="rythme in content.rythmes" :key="rythme.id" :value="rythme.id">
                  {{ rythme.libelle }}
                </option>
              </select>
              <div v-if="f.preliminaire" class="invalid-feedback">{{ content?.catalogueTraduction?.catalogueObligatoire || $t('catalogue.obligatoire') }}</div>
            </div>

            <div
                v-else-if="f.id === constants.filtre.FILTRE_AUTONOMIE && f.actif && (filtre.preFiltreLoad || f.preliminaire)"
                id="filtreAutonomie" class="form-group me-3">
              <select id="autonomie" v-model="filtre.autonomie" :required="f.preliminaire" class="form-select"
                      name="autonomie" @blur.passive="onFiltreInputBlur(f.preliminaire, $event)"
                      @change.passive="onFilter()">
                <option value="">{{ content?.catalogueTraduction?.tousAutonomies || $t('catalogue.tous.autonomies') }}</option>
                <option v-for="autonomie in content.autonomies" :key="autonomie.id" :value="autonomie.id">
                  {{ autonomie.libelle }}
                </option>
              </select>
              <div v-if="f.preliminaire" class="invalid-feedback">{{ content?.catalogueTraduction?.catalogueObligatoire || $t('catalogue.obligatoire') }}</div>
            </div>

            <template
                v-else-if="f.id === constants.filtre.FILTRE_LIEU_DEPART && f.actif && (filtre.preFiltreLoad || f.preliminaire)">
              <div id="filtreDepart" class="form-group me-3">
                <select id="lieuDepart" v-model="filtre.lieuDepart" :required="f.preliminaire" class="form-select"
                        name="lieuDepart" @blur.passive="onFiltreInputBlur(f.preliminaire, $event)"
                        @change.passive="onFilter()">
                  <option value="">{{ content?.catalogueTraduction?.tousLieuxDeparts || $t('catalogue.tous.lieuxDeparts') }}</option>
                  <option value="0">{{ content?.catalogueTraduction?.catalogueAucunTransport || $t('catalogue.aucunTransport') }}</option>
                  <option v-for="zgd in content.zoneGeographiqueDepart" :key="zgd.id" :value="zgd.id">{{
                      zgd.libelle
                    }}
                  </option>
                </select>
                <div v-if="f.preliminaire" class="invalid-feedback">{{ content?.catalogueTraduction?.catalogueObligatoire || $t('catalogue.obligatoire') }}</div>
              </div>
            </template>

            <div
                v-else-if="f.id === constants.filtre.FILTRE_LIEU && f.actif && (filtre.preFiltreLoad || f.preliminaire)"
                id="filtreLieu" class="form-group me-3">
              <select id="lieuActivite" v-model="filtre.lieuActivite" :required="f.preliminaire" class="form-select"
                      name="lieuActivite" @blur.passive="onFiltreInputBlur(f.preliminaire, $event)"
                      @change.passive="onFilter()">
                <option value="">{{ content?.catalogueTraduction?.tousLieuxActivite || $t('catalogue.tous.lieuxActivite') }}</option>
                <option v-for="zgd in content.zoneGeographiqueActivite" :key="zgd.id" :value="zgd.id">{{ zgd.libelle }}
                </option>
              </select>
              <div v-if="f.preliminaire" class="invalid-feedback">{{ content?.catalogueTraduction?.catalogueObligatoire || $t('catalogue.obligatoire') }}</div>
            </div>

            <div
                v-else-if="f.id === constants.filtre.FILTRE_REGIONAL && f.actif && (filtre.preFiltreLoad || f.preliminaire)"
                id="filtreRegional" class="form-group me-3">
              <select id="regional" v-model="filtre.regional" :required="f.preliminaire" class="form-select"
                      name="regional" @blur.passive="onFiltreInputBlur(f.preliminaire, $event)"
                      @change.passive="onFilter()">
                <option value="">{{ content?.catalogueTraduction?.tousRegionales || $t('catalogue.tous.regionales') }}</option>
                <option v-for="regional in content.regionals" :key="regional.id" :value="regional.id">{{ regional.libelleWeb || regional.nom }}
                </option>
              </select>
              <div v-if="f.preliminaire" class="invalid-feedback">{{ content?.catalogueTraduction?.catalogueObligatoire || $t('catalogue.obligatoire') }}</div>
            </div>
          </template>
        </form>
      </nav>

      <div v-if="filtre.preFiltreLoad || !content.filtrePreliminaire" class="ns-center">
        <div v-if="content.searchResultsTotal && content.activities?.length > 0"
             class="text-white me-3 compteur-resultats">
          {{ content?.catalogueTraduction?.catalogueResultats || $t('catalogue.resultats') }}&nbsp;{{ content.activities.length }}&nbsp;/&nbsp;{{
            content.searchResultsTotal
          }}
        </div>

        <button class="ns-btn-refresh" type="reset" @click="onReset">
          <svg fill="none" height="20" viewBox="0 0 17 20" width="17" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M8.5 4V0L13.5 5L8.5 10V6C5.19 6 2.5 8.69 2.5 12C2.5 15.31 5.19 18 8.5 18C11.81 18 14.5 15.31 14.5 12H16.5C16.5 16.42 12.92 20 8.5 20C4.08 20 0.5 16.42 0.5 12C0.5 7.58 4.08 4 8.5 4Z"
                fill="#F7A620"/>
          </svg>
          {{ content?.catalogueTraduction?.catalogueBtnReset || $t('catalogue.btnReset') }}
        </button>
      </div>

      <template v-if="filtre.preFiltreLoad || !content.filtrePreliminaire">

        <div v-if="searching" class="d-flex justify-content-center m-5">
          <div class="spinner-border" role="status" style="width: 3rem; height: 3rem;">
            <span class="visually-hidden">{{ content?.catalogueTraduction?.globalChargement || $t('global.chargement') }}</span>
          </div>
        </div>
        <div v-else-if="content.activities?.length > 0" ref="searchResults"
             class="resultats row row-cols-lg-3 row-cols-md-2 row-cols-sm-1 mt-4 mb-3">
          <div v-for="activity in content.activities" :key="activity.id" class="col">
            <Vignette :activity="activity" :idCatalogue="content.id" :catalogueTraduction="content?.catalogueTraduction"/>
          </div>
        </div>
        <div v-else-if="content.aucuneCampagneActives && content.aucuneCampagne" class="message mt-3">
          <div v-html="content.aucuneCampagne"></div>
        </div>
        <div v-else-if="content.aucunContenu" class="message mt-3">
          <div v-html="content.aucunContenu"></div>
        </div>
        <div v-if="fetching && !searching" class="d-flex justify-content-center m-5">
          <div class="spinner-border" role="status" style="width: 3rem; height: 3rem;">
            <span class="visually-hidden">{{ content?.catalogueTraduction?.globalChargement || $t('global.chargement') }}</span>
          </div>
        </div>
      </template>
    </div>
  </template>
</template>

<script>
import {useStore} from "vuex";
import {nextTick, onBeforeMount, onBeforeUnmount, onMounted, ref} from "vue";
import axios from 'axios';
import {fr} from 'date-fns/locale';
import constants from "@/constants";
import Vignette from "@/components/Vignette";
import {useTooltips} from "@/utils";
import {useRouter} from "vue-router";

export default {
  name: "Catalogue",
  components: {Vignette},
  props: ['catalogue'],
  setup(props) {
    const router = useRouter();
    const idTheme = ref(router.currentRoute?.value?.query?.theme);
    const store = useStore();
    const formFiltres = ref(null);
    const searchResults = ref(null);
    const refHeight = ref(null);
    const loading = ref(true);
    const content = ref({});
    const hasError = ref(false);
    const filtre = ref({
      motCle: null,
      typePublic: '',
      theme: '',
      dateDebut: null,
      dateFin: null,
      accesHandicap: null,
      mobilite: '',
      autonomie: '',
      rythme: '',
      anneeNaissance: null,
      lieuDepart: '',
      lieuActivite: '',
      regional: '',
      preFiltreLoad: false,
    });
    const searching = ref(false);
    const fetching = ref(false);
    const stopFetching = ref(false);
    const widgetURL = process.env.VUE_APP_WIDGET_API_SERVER;
    const searchLimit = ref(constants.catalogue.SEARCH_LIMIT);
    const searchOffset = ref(constants.catalogue.SEARCH_OFFSET);
    const datesInterval = ref(null);

    onBeforeMount(() => {
      addEventListener("scroll", handleScroll, {passive: true});
      addEventListener("resize", handleHeight, {passive: true});
      addEventListener("message", handleMessage, {passive: true});
    });

    onBeforeUnmount(() => {
      removeEventListener("scroll", handleScroll);
      removeEventListener("resize", handleHeight);
      removeEventListener("message", handleMessage);
    });

    onMounted(async () => {
      if (props.catalogue > 0) {
        if (sessionStorage && sessionStorage.getItem('filtres')) {
          filtre.value = JSON.parse(sessionStorage.getItem('filtres'));
        }
        if (idTheme.value)
          filtre.value.theme = idTheme.value;
        await fetchCatalogue();
      } else {
        loading.value = false;
        hasError.value = true;
      }
    });

    const handleMessage = (e) => {
      if (e.data?.bottom) {
        stopFetching.value = content.value.activities?.length >= content.value.searchResultsTotal;
        if (!fetching.value && !stopFetching.value) {
          searchOffset.value += searchLimit.value;
          fetchActivities();
        }
      }
    };

    const handleHeight = async () => {
      await nextTick();
      let height = refHeight.value?.offsetHeight + 50;
      window.parent.postMessage({frameHeight: height}, '*');
    };

    const handleScroll = () => {
      let scroll = searchResults.value;
      stopFetching.value = content.value.activities?.length >= content.value.searchResultsTotal;
      if (!fetching.value && !stopFetching.value) {
        if (scroll?.getBoundingClientRect()?.bottom < innerHeight) {
          searchOffset.value += searchLimit.value;
          fetchActivities();
        }
      }
    };

    const fetchCatalogue = async () => {
      searchLimit.value = constants.catalogue.SEARCH_LIMIT;
      searchOffset.value = constants.catalogue.SEARCH_OFFSET;
      if (!filtre.value.accesHandicap)
        filtre.value.accesHandicap = null;
      if (datesInterval.value) {
        filtre.value.dateDebut = datesInterval.value[0];
        filtre.value.dateFin = datesInterval.value[1];
      }
      await axios
          .post(process.env.VUE_APP_WIDGET_API_ROOT + '/search/' + props.catalogue, filtre.value, {
            params: {
              searchLimit: searchLimit.value,
              searchOffset: searchOffset.value
            }
          })
          .then(response => {
            content.value = response.data;
            if (!filtre.value.preFiltreLoad)
              filtre.value.preFiltreLoad = !content.value.filtrePreliminaire;
            store.commit('updateFiltres', filtre.value);
            sessionStorage?.setItem('filtres', JSON.stringify(filtre.value));
            searching.value = false;
          })
          .catch(error => {
            console.error(error);
            hasError.value = true;
          })
          .finally(() => {
            loading.value = false;
          });

      await handleHeight();
      await useTooltips();
    };

    const fetchActivities = async () => {
      fetching.value = true;
      if (datesInterval.value) {
        filtre.value.dateDebut = datesInterval.value[0];
        filtre.value.dateFin = datesInterval.value[1];
      }
      await axios
          .post(process.env.VUE_APP_WIDGET_API_ROOT + '/search/' + props.catalogue, filtre.value, {
            params: {
              searchLimit: searchLimit.value,
              searchOffset: searchOffset.value
            }
          })
          .then(response => {
            const newActivities = response.data?.activities;
            if (newActivities?.length > 0)
              content.value.activities.push(...newActivities);
          })
          .catch(error => {
            console.error(error);
          }).finally(() => {
            fetching.value = false;
          });

      await handleHeight();
      await useTooltips();
    };

    const onFiltreInputBlur = (validate, e) => {
      if (validate) {
        let input = e.target.parentElement;
        input.classList.add('was-validated');
      }
    };

    const onToggleCalendar = async (e) => {
      const $el = e.currentTarget;
      await handleHeight();
      if ($el?.closest('.ns-btn')?.classList?.contains('is-open') && datesInterval.value)
        onFilter(e);
      $el?.closest('.ns-btn')?.classList?.toggle('is-open');
    };

    const onReset = async (e) => {
      let errors = 0;
      e.preventDefault();
      Array.prototype.slice.call(formFiltres.value).forEach((input) => {
        if (!input.checkValidity()) {
          errors++;
          input.parentElement.classList.add('was-validated');
          e.stopPropagation();
        }
      });
      if (errors === 0) {
        filtre.value = store.getters.getFiltres;
        if (content?.value?.filtres.length > 0) {
          // remettre les filtres qui ne sont pas préliminaire à zéro.
          content.value.filtres.filter(f => {
            return !f.preliminaire;
          }).forEach(f => {
            if (f.id === constants.filtre.FILTRE_MOT_CLE)
              filtre.value.motCle = null;
            else if (f.id === constants.filtre.FILTRE_DATE) {
              filtre.value.dateDebut = null;
              filtre.value.dateFin = null;
              datesInterval.value = null;
            } else if (f.id === constants.filtre.FILTRE_AGE)
              filtre.value.anneeNaissance = null;
            else if (f.id === constants.filtre.FILTRE_PUBLIC)
              filtre.value.typePublic = '';
            else if (f.id === constants.filtre.FILTRE_HANDICAPE)
              filtre.value.accesHandicap = null;
            else if (f.id === constants.filtre.FILTRE_MOBILITE)
              filtre.value.mobilite = '';
            else if (f.id === constants.filtre.FILTRE_AUTONOMIE)
              filtre.value.autonomie = '';
            else if (f.id === constants.filtre.FILTRE_RYTHME)
              filtre.value.rythme = '';
            else if (f.id === constants.filtre.FILTRE_THEME)
              filtre.value.theme = '';
            else if (f.id === constants.filtre.FILTRE_LIEU_DEPART)
              filtre.value.lieuDepart = '';
            else if (f.id === constants.filtre.FILTRE_LIEU)
              filtre.value.lieuActivite = '';
            else if (f.id === constants.filtre.FILTRE_REGIONAL)
              filtre.value.regional = '';
          });
        }
        await fetchCatalogue();
      }
    };

    const onFilter = async () => {
      let errors = 0;
      Array.prototype.slice.call(formFiltres.value).forEach((input) => {
        if (!input.checkValidity()) {
          errors++;
          input.parentElement.classList.add('was-validated');
        }
      });
      if (errors === 0) {
        searching.value = true;
        filtre.value.preFiltreLoad = true;
        await fetchCatalogue();
      }
    };

    return {
      formFiltres,
      searchResults,
      filtre,
      loading,
      hasError,
      content,
      searching,
      constants,
      onFilter,
      onReset,
      onFiltreInputBlur,
      widgetURL,
      fetching,
      onToggleCalendar,
      datesInterval,
      fr,
      refHeight,
    };
  }
}
</script>

<style scoped>
i.bi-asterisk {
  font-size: x-small;
}

div.error-container, div.loading-container {
  padding: 100px;
}
</style>
